// eslint-disable-next-line max-classes-per-file
import { v4 } from 'uuid';
import { onUnmounted, shallowRef, triggerRef } from 'vue';

type EventCallback = (...args: any[]) => void;

export class EventEmitter {
  private events: Map<string, Set<EventCallback>> = new Map();

  on(event: string, callback: EventCallback): () => void {
    if (!this.events.has(event)) {
      this.events.set(event, new Set());
    }
    this.events.get(event)?.add(callback);

    return () => this.off(event, callback);
  }

  off(event: string, callback: EventCallback): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.delete(callback);
    }
  }

  emit(event: string, ...args: any[]): void {
    const callbacks = this.events.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(...args));
    }
  }

  clear(): void {
    this.events.clear();
  }
}

interface AlertProps {
  type: string;
  duration: number;
  message: string;
  namespace?: string;
}

class Alert {
  public readonly id: string;

  public readonly type: string;

  public readonly message: string;

  public readonly duration: number;

  public readonly namespace?: string;

  public isVisible = false;

  private timeout?: NodeJS.Timeout;

  constructor({ type, duration, message, namespace }: AlertProps) {
    this.id = v4();
    this.type = type;
    this.duration = duration;
    this.message = message;
    this.namespace = namespace;
  }

  show() {
    this.isVisible = true;

    this.timeout = setTimeout(() => {
      this.hide();
    }, this.duration);
  }

  hide() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    this.isVisible = false;
  }
}

class AlertQueue {
  private queue: Alert[] = [];

  private currentAlert: Alert | null = null;

  private isProcessing = false;

  readonly emitter = new EventEmitter();

  enqueue(props: AlertProps) {
    const alert = new Alert(props);

    this.queue.push(alert);

    this.process();

    return alert;
  }

  dequeue() {
    return this.queue.shift() || null;
  }

  remove(id: string) {
    const index = this.queue.findIndex(alert => alert.id === id);
    if (index !== -1) {
      this.queue[index].hide();
      this.queue.splice(index, 1);
      return true;
    }

    if (this.currentAlert?.id === id) {
      this.currentAlert.hide();
      this.currentAlert = null;
      this.isProcessing = false;
      this.process();
      return true;
    }

    return false;
  }

  *generator() {
    while (this.queue.length > 0) {
      const alert = this.dequeue();

      if (alert) {
        yield alert;
      }
    }
  }

  async process() {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    this.isProcessing = true;

    for (const alert of this.generator()) {
      this.currentAlert = alert;

      this.currentAlert.show();

      this.emitter.emit('change', { currentAlert: alert });

      // eslint-disable-next-line no-await-in-loop
      await new Promise<void>(resolve => {
        const checkHidden = () => {
          if (!alert.isVisible) {
            this.emitter.emit('change', { currentAlert: alert });
            resolve();
          } else {
            setTimeout(checkHidden, 100);
          }
        };
        checkHidden();
      });

      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => {
        setTimeout(resolve, 300);
      });
    }

    this.currentAlert = null;
    this.isProcessing = false;

    this.emitter.emit('change', { currentAlert: this.current });
  }

  get current() {
    return this.currentAlert;
  }
}

class AlertManager {
  // eslint-disable-next-line no-use-before-define
  private static instance: AlertManager;

  private queue: AlertQueue;

  readonly emitter = new EventEmitter();

  private constructor() {
    this.queue = new AlertQueue();

    this.queue.emitter.on('change', payload => {
      this.emitter.emit('change', payload);
    });
  }

  static getInstance() {
    if (!AlertManager.instance) {
      AlertManager.instance = new AlertManager();
    }
    return AlertManager.instance;
  }

  show(props: AlertProps) {
    return this.queue.enqueue(props);
  }

  hide(id: string) {
    return this.queue.remove(id);
  }

  get current() {
    return this.queue.current;
  }
}

export function useAlert(namespace?: string) {
  const alertManager = AlertManager.getInstance();

  const getFilteredCurrent = () => {
    const { current } = alertManager;
    if (!current) return null;
    if (current.namespace === namespace) return current;
    return null;
  };

  const currentAlert = shallowRef<Alert | null>(getFilteredCurrent());

  const unsubscribe = alertManager.emitter.on('change', payload => {
    const globalCurrent = payload.currentAlert;

    if (!globalCurrent) {
      currentAlert.value = null;
    } else if (globalCurrent.namespace === namespace) {
      currentAlert.value = globalCurrent;
    } else {
      currentAlert.value = null;
    }

    // Manually force Vue to update.
    // This tells Vue: "I know the object reference might be the same,
    // but its internal content has changed, so please re-render."
    triggerRef(currentAlert);
  });

  onUnmounted(() => {
    unsubscribe();
  });

  return {
    currentAlert,
    show: (props: AlertProps) => alertManager.show({ ...props, namespace }),
    hide: alertManager.hide.bind(alertManager),
  };
}
